/**
 * Yjs array utility functions
 * Handles common patterns for updating Yjs arrays (find, update, delete, insert)
 */

import * as Y from "yjs";

/**
 * Update an item in a Y.Array by finding it by ID
 * Uses the pattern: findIndex -> delete -> insert
 * Returns true if the item was found and updated, false otherwise
 */
export function updateYArrayItem<T extends { id: string }>(
  array: Y.Array<T>,
  id: string,
  updater: (item: T) => T
): boolean {
  const items = array.toArray();
  const index = items.findIndex((item) => item.id === id);

  if (index === -1) {
    return false;
  }

  const updatedItem = updater(items[index]);
  array.delete(index, 1);
  array.insert(index, [updatedItem]);

  return true;
}

/**
 * Delete an item from a Y.Array by ID
 * Returns true if the item was found and deleted, false otherwise
 */
export function deleteYArrayItem<T extends { id: string }>(
  array: Y.Array<T>,
  id: string
): boolean {
  const items = array.toArray();
  const index = items.findIndex((item) => item.id === id);

  if (index === -1) {
    return false;
  }

  array.delete(index, 1);
  return true;
}

/**
 * Delete multiple items from a Y.Array by IDs
 * Returns the number of items deleted
 */
export function deleteYArrayItems<T extends { id: string }>(
  array: Y.Array<T>,
  ids: string[]
): number {
  if (ids.length === 0) {
    return 0;
  }

  const items = array.toArray();

  // Map to indices first, then sort descending to delete safely
  const indicesToDelete = items
    .map((item, index) => ({ id: item.id, index }))
    .filter(({ id }) => ids.includes(id))
    .map(({ index }) => index)
    .sort((a, b) => b - a);

  indicesToDelete.forEach((index) => {
    array.delete(index, 1);
  });

  return indicesToDelete.length;
}

/**
 * Update multiple items in a Y.Array by IDs
 * Returns the number of items updated
 */
export function updateYArrayItems<T extends { id: string }>(
  array: Y.Array<T>,
  ids: string[],
  updater: (item: T) => T
): number {
  const items = array.toArray();

  // Create ID to index map
  const idToIndex = new Map<string, number>();
  items.forEach((item, i) => idToIndex.set(item.id, i));

  let updatedCount = 0;

  ids.forEach((id) => {
    const index = idToIndex.get(id);
    if (index !== undefined) {
      const item = items[index];
      const updatedItem = updater(item);
      array.delete(index, 1);
      array.insert(index, [updatedItem]);
      updatedCount++;
    }
  });

  return updatedCount;
}

/**
 * Find an item in a Y.Array by ID
 */
export function findYArrayItem<T extends { id: string }>(
  array: Y.Array<T>,
  id: string
): T | undefined {
  const items = array.toArray();
  return items.find((item) => item.id === id);
}
